<script>
  import { createEventDispatcher, onMount, onDestroy } from "svelte";
  import Search from "carbon-icons-svelte/lib/Search.svelte";
  import Checkmark from "carbon-icons-svelte/lib/Checkmark.svelte";
  import DeliveryTruck from "carbon-icons-svelte/lib/DeliveryTruck.svelte";
  import DocumentAdd from "carbon-icons-svelte/lib/DocumentAdd.svelte";
  import WarningFilled from "carbon-icons-svelte/lib/WarningFilled.svelte";
  import ChevronLeft from "carbon-icons-svelte/lib/ChevronLeft.svelte";
  import ChevronDown from "carbon-icons-svelte/lib/ChevronDown.svelte";
  import Filter from "carbon-icons-svelte/lib/Filter.svelte";
  import Time from "carbon-icons-svelte/lib/Time.svelte";
  import Close from "carbon-icons-svelte/lib/Close.svelte";
  import {
    getJobCustomerName,
    getJobSiteAddress,
    getJobTotalTonnes,
    getJobTotalAllocatedTrucks,
    formatTonnes
  } from "./jobMaterialUtils.js";

  export let isSidebarOpen = true;
  export let jobsList = [];
  export let currentJob = null;
  export let isLoadingJobs = false;
  export let jobsError = "";
  export let quotes = [];
  export let companies = [];

  const dispatch = createEventDispatcher();

  const DEFAULT_STATUSES = ["pending", "proposed", "active"];

  let searchQuery = "";
  let selectedStatuses = [...DEFAULT_STATUSES];
  let isStatusDropdownOpen = false;
  let multiselectContainer = null;

  const STATUS_OPTIONS = [
    { value: "pending", label: "Pending", color: "#b45309", bg: "#fef3c7", border: "#fde68a" },
    { value: "proposed", label: "Proposed", color: "#0369a1", bg: "#e0f2fe", border: "#bae6fd" },
    { value: "active", label: "Active", color: "#15803d", bg: "#dcfce7", border: "#bbf7d0" },
    { value: "completed", label: "Completed", color: "#475569", bg: "#f1f5f9", border: "#cbd5e1" },
    { value: "cancelled", label: "Cancelled", color: "#b91c1c", bg: "#fee2e2", border: "#fecaca" }
  ];

  function toggleStatusFilter(statusVal) {
    if (selectedStatuses.includes(statusVal)) {
      selectedStatuses = selectedStatuses.filter(s => s !== statusVal);
    } else {
      selectedStatuses = [...selectedStatuses, statusVal];
    }
  }

  function resetStatusToDefault() {
    selectedStatuses = [...DEFAULT_STATUSES];
  }

  function selectAllStatuses() {
    selectedStatuses = STATUS_OPTIONS.map(o => o.value);
  }

  function clearAllStatuses() {
    selectedStatuses = [];
  }

  function handleClickOutside(event) {
    if (isStatusDropdownOpen && multiselectContainer && !multiselectContainer.contains(event.target)) {
      isStatusDropdownOpen = false;
    }
  }

  function handleWindowKeydown(event) {
    if (event.key === "Escape" && isStatusDropdownOpen) {
      isStatusDropdownOpen = false;
    }
  }

  onMount(() => {
    if (typeof window !== "undefined") {
      window.addEventListener("click", handleClickOutside);
      window.addEventListener("keydown", handleWindowKeydown);
    }
  });

  onDestroy(() => {
    if (typeof window !== "undefined") {
      window.removeEventListener("click", handleClickOutside);
      window.removeEventListener("keydown", handleWindowKeydown);
    }
  });

  $: statusCounts = jobsList.reduce((acc, j) => {
    const st = (j.status || "proposed").toLowerCase().trim();
    acc[st] = (acc[st] || 0) + 1;
    return acc;
  }, {});

  $: isDefaultStatuses =
    selectedStatuses.length === DEFAULT_STATUSES.length &&
    DEFAULT_STATUSES.every(s => selectedStatuses.includes(s));

  $: filteredJobs = jobsList.filter((j) => {
    if (selectedStatuses.length === 0) return false;
    const jStatus = (j.status || "proposed").toLowerCase().trim();
    if (!selectedStatuses.includes(jStatus)) return false;

    if (!searchQuery.trim()) return true;
    const term = searchQuery.toLowerCase().trim();
    const jNum = String(j.__job_number__ || j.id || "").toLowerCase();
    const qRef = String(j.quote_number || (j.quote_id ? `qt-${j.quote_id}` : "")).toLowerCase();
    const custName = String(
      (typeof j.customer === "object" ? j.customer?.name : j.customer) || ""
    ).toLowerCase();
    const poNum = String(j.po_number || "").toLowerCase();
    const site = String(
      j.address?.street || (typeof j.customer === "object" ? j.customer?.address?.street : "") || ""
    ).toLowerCase();
    const mats = (j.materials || [])
      .map((m) => m.name || "")
      .join(" ")
      .toLowerCase();

    return (
      jNum.includes(term) ||
      qRef.includes(term) ||
      custName.includes(term) ||
      poNum.includes(term) ||
      site.includes(term) ||
      mats.includes(term)
    );
  });
</script>

<aside class="quotes-sidebar {isSidebarOpen ? 'open' : 'collapsed'}">
  <div class="sidebar-header">
    <!-- Top Actions Row: Status Multi-Select Filter & Collapse Chevron -->
    <div class="sidebar-actions-row">
      <div class="status-multiselect-wrap" bind:this={multiselectContainer}>
        <!-- Native multi-select for accessibility and testability -->
        <select
          multiple
          id="job-status-filter-select"
          class="sr-only"
          bind:value={selectedStatuses}
          aria-label="Filter jobs by status"
        >
          {#each STATUS_OPTIONS as opt}
            <option value={opt.value}>{opt.label}</option>
          {/each}
        </select>

        <!-- Custom Multi-Select Trigger Button -->
        <button
          type="button"
          class="status-multiselect-trigger {isStatusDropdownOpen ? 'open' : ''}"
          on:click={() => (isStatusDropdownOpen = !isStatusDropdownOpen)}
          aria-haspopup="listbox"
          aria-expanded={isStatusDropdownOpen}
          title="Click to filter jobs by status"
        >
          <div class="trigger-label-group">
            <Filter size={13} class="filter-icon" />
            <span class="trigger-title">Status:</span>
            <div class="trigger-chips-container">
              {#if selectedStatuses.length === 0}
                <span class="placeholder-text">None selected</span>
              {:else if selectedStatuses.length === STATUS_OPTIONS.length}
                <span class="status-chip chip-all">All ({filteredJobs.length})</span>
              {:else if selectedStatuses.length <= 3}
                {#each selectedStatuses as st}
                  {@const opt = STATUS_OPTIONS.find(o => o.value === st)}
                  {#if opt}
                    <span
                      class="status-chip"
                      style="background: {opt.bg}; color: {opt.color}; border: 1px solid {opt.border};"
                    >
                      <span class="chip-dot" style="background: {opt.color};"></span>
                      <span class="chip-text">{opt.label}</span>
                      <button
                        type="button"
                        class="chip-remove"
                        on:click|stopPropagation={() => toggleStatusFilter(opt.value)}
                        title="Remove {opt.label}"
                      >
                        &times;
                      </button>
                    </span>
                  {/if}
                {/each}
              {:else}
                {@const firstOpt = STATUS_OPTIONS.find(o => o.value === selectedStatuses[0])}
                {#if firstOpt}
                  <span
                    class="status-chip"
                    style="background: {firstOpt.bg}; color: {firstOpt.color}; border: 1px solid {firstOpt.border};"
                  >
                    <span class="chip-dot" style="background: {firstOpt.color};"></span>
                    <span class="chip-text">{firstOpt.label}</span>
                  </span>
                {/if}
                <span class="status-chip chip-more" title={selectedStatuses.map(s => s[0].toUpperCase() + s.slice(1)).join(', ')}>
                  +{selectedStatuses.length - 1} more
                </span>
              {/if}
            </div>
          </div>
          <div class="trigger-arrow">
            <ChevronDown size={14} class="chevron {isStatusDropdownOpen ? 'rotated' : ''}" />
          </div>
        </button>

        <!-- Multi-Select Dropdown Menu -->
        {#if isStatusDropdownOpen}
          <div class="status-dropdown-menu" role="listbox">
            <div class="dropdown-header">
              <span class="dropdown-heading">Filter by Status</span>
              <div class="dropdown-quick-links">
                <button type="button" class="quick-link" on:click={resetStatusToDefault} title="Reset to Pending, Proposed, and Active">
                  Default
                </button>
                <span class="quick-bullet">&bull;</span>
                <button type="button" class="quick-link" on:click={selectAllStatuses}>
                  All
                </button>
                <span class="quick-bullet">&bull;</span>
                <button type="button" class="quick-link" on:click={clearAllStatuses}>
                  Clear
                </button>
              </div>
            </div>
            <div class="dropdown-items-list">
              {#each STATUS_OPTIONS as opt}
                {@const isChecked = selectedStatuses.includes(opt.value)}
                {@const count = statusCounts[opt.value] || 0}
                <label class="dropdown-item-row {isChecked ? 'checked' : ''}">
                  <input
                    type="checkbox"
                    checked={isChecked}
                    on:change={() => toggleStatusFilter(opt.value)}
                    class="status-checkbox"
                  />
                  <span class="status-indicator-dot" style="background: {opt.color};"></span>
                  <span class="status-name">{opt.label}</span>
                  <span class="status-item-count" class:has-items={count > 0}>({count})</span>
                </label>
              {/each}
            </div>
          </div>
        {/if}
      </div>

      <button
        class="sidebar-close-btn"
        on:click={() => dispatch("toggleSidebar")}
        title="Slide sidebar out"
        type="button"
        aria-label="Collapse Sidebar"
      >
        <ChevronLeft size={18} />
      </button>
    </div>

    <!-- Search Bar -->
    <div class="search-box">
      <Search size={16} class="search-icon" />
      <input
        type="text"
        placeholder="Search jobs by #, client, material..."
        bind:value={searchQuery}
        class="search-input"
      />
      {#if searchQuery}
        <button class="clear-btn" on:click={() => (searchQuery = "")} type="button">&times;</button>
      {/if}
    </div>

    <!-- Filter Summary Row -->
    <div class="filter-summary-row">
      <span class="filter-count-text">
        Showing <strong>{filteredJobs.length}</strong> of {jobsList.length} jobs
      </span>
      {#if !isDefaultStatuses}
        <button type="button" class="btn-reset-default" on:click={resetStatusToDefault}>
          Reset to default
        </button>
      {/if}
    </div>
  </div>

  <!-- Jobs List Container -->
  <div class="quotes-list-scroll">
    {#if isLoadingJobs && jobsList.length === 0}
      <div class="loading-state">
        <span class="spinner" />
        <p>Loading database jobs...</p>
      </div>
    {:else if jobsError}
      <div class="error-state">
        <WarningFilled size={24} style="color: var(--tt-danger, #e53935);" />
        <p>{jobsError}</p>
        <button class="btn btn-sm btn-outline" on:click={() => dispatch("reloadJobs")} type="button">Try Again</button>
      </div>
    {:else if filteredJobs.length === 0}
      <div class="empty-list-state">
        {#if selectedStatuses.length === 0}
          <p class="empty-msg-title">No statuses selected</p>
          <p class="empty-msg-sub">Select one or more statuses from the filter above to display jobs.</p>
          <button class="btn btn-xs btn-primary mt-2" on:click={resetStatusToDefault} type="button">
            Select Pending, Proposed &amp; Active
          </button>
        {:else if searchQuery}
          <p class="empty-msg-title">No jobs match your search</p>
          <button class="btn btn-xs btn-ghost mt-2" on:click={() => (searchQuery = '')} type="button">
            Clear Search
          </button>
        {:else}
          <p class="empty-msg-title">No matching jobs found</p>
          <p class="empty-msg-sub">No jobs with status: {selectedStatuses.map(s => s[0].toUpperCase() + s.slice(1)).join(', ')}</p>
          <button class="btn btn-xs btn-outline mt-2" on:click={selectAllStatuses} type="button">
            Show All Statuses
          </button>
        {/if}
      </div>
    {:else}
      {#each filteredJobs as j (j.id || j._id || j.__job_number__)}
        {@const isSelected = currentJob && (currentJob.id === j.id || currentJob.__job_number__ === j.__job_number__)}
        {@const jStatus = (j.status || "proposed").toLowerCase()}
        {@const totalAllocated = getJobTotalAllocatedTrucks(j)}
        <button
          class="quote-card job-item-card {isSelected ? 'selected' : ''}"
          on:click={() => dispatch("selectJob", j)}
          type="button"
        >
          <div class="card-top-row">
            <div class="job-card-badges">
              <span class="quote-number-pill job-number-pill">
                #{j.__job_number__ || j.id}
              </span>
              {#if j.quote_number || j.quote_id}
                <span class="quote-ref-pill">
                  {j.quote_number || `QT-${j.quote_id}`}
                </span>
              {/if}
            </div>

            <span class="status-pill status-{jStatus}">
              {#if jStatus === 'active'}
                <DeliveryTruck size={12} /> Active
              {:else if jStatus === 'completed'}
                <Checkmark size={12} /> Completed
              {:else if jStatus === 'pending'}
                <Time size={12} /> Pending
              {:else if jStatus === 'cancelled'}
                <Close size={12} /> Cancelled
              {:else}
                <DocumentAdd size={12} /> Proposed
              {/if}
            </span>
          </div>

          <div class="card-client-row">
            <span class="client-name">{getJobCustomerName(j, quotes, companies)}</span>
          </div>

          <div class="card-site-preview">
            <span class="site-text">{getJobSiteAddress(j, quotes)}</span>
          </div>

          <div class="card-materials-chips">
            {#each (j.materials || []).slice(0, 3) as mat}
              <span class="mat-chip">{mat.name || "Material"} ({mat.amount || 0}t)</span>
            {/each}
            {#if (j.materials || []).length > 3}
              <span class="mat-chip-more">+{(j.materials || []).length - 3}</span>
            {/if}
          </div>

          <div class="card-footer-row">
            <div class="metric-pair">
              <span class="metric-label">Tonnes:</span>
              <span class="metric-val">{formatTonnes(getJobTotalTonnes(j))}</span>
            </div>
            <div class="metric-pair">
              <span class="metric-label">Allocated:</span>
              <span class="metric-val trucks-val">
                <DeliveryTruck size={13} style="vertical-align: -2px; margin-right: 3px;" />
                {totalAllocated} {totalAllocated === 1 ? 'truck' : 'trucks'}
              </span>
            </div>
          </div>
        </button>
      {/each}
    {/if}
  </div>
</aside>

<style>
  .quotes-sidebar {
    width: 380px;
    min-width: 380px;
    max-width: 380px;
    background: #ffffff;
    border-right: 1px solid var(--tt-slate-200, #e2e8f0);
    display: flex;
    flex-direction: column;
    overflow: hidden;
    flex-shrink: 0;
    transition: margin-left 0.28s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.22s ease;
    position: relative;
    z-index: 10;
  }

  .quotes-sidebar.collapsed {
    margin-left: -380px;
    pointer-events: none;
    opacity: 0;
  }

  .sidebar-header {
    padding: 1rem 1.25rem 0.75rem 1.25rem;
    border-bottom: 1px solid var(--tt-slate-200, #e2e8f0);
    background: #ffffff;
    position: relative;
    z-index: 30;
  }

  .sidebar-actions-row {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 0.5rem;
  }

  .sidebar-close-btn {
    width: 28px;
    height: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: transparent;
    border: 1px solid transparent;
    border-radius: 6px;
    color: var(--tt-slate-500, #64748b);
    cursor: pointer;
    transition: all 0.15s ease;
    padding: 0;
  }

  .sidebar-close-btn:hover {
    background: var(--tt-slate-100, #f1f5f9);
    color: var(--tt-slate-900, #0f172a);
  }

  .status-multiselect-wrap {
    flex: 1;
    position: relative;
    min-width: 0;
  }

  .status-multiselect-trigger {
    width: 100%;
    min-height: 36px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.25rem 0.55rem;
    background: var(--tt-slate-50, #f8fafc);
    border: 1px solid var(--tt-slate-300, #cbd5e1);
    border-radius: 6px;
    cursor: pointer;
    transition: all 0.15s ease;
    text-align: left;
  }

  .status-multiselect-trigger:hover {
    background: #ffffff;
    border-color: var(--tt-primary, #2563eb);
  }

  .status-multiselect-trigger.open {
    background: #ffffff;
    border-color: var(--tt-primary, #2563eb);
    box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.18);
  }

  .trigger-label-group {
    display: flex;
    align-items: center;
    gap: 0.35rem;
    flex: 1;
    min-width: 0;
    overflow: hidden;
  }

  :global(.filter-icon) {
    color: var(--tt-slate-500, #64748b);
    flex-shrink: 0;
  }

  .trigger-title {
    font-size: 0.72rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    color: var(--tt-slate-600, #475569);
    flex-shrink: 0;
  }

  .trigger-chips-container {
    display: flex;
    align-items: center;
    gap: 0.25rem;
    min-width: 0;
    overflow: hidden;
    white-space: nowrap;
  }

  .status-chip {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    font-size: 0.68rem;
    font-weight: 600;
    padding: 0.1rem 0.35rem;
    border-radius: 4px;
    line-height: 1.2;
    white-space: nowrap;
    flex-shrink: 0;
  }

  .chip-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    flex-shrink: 0;
  }

  .chip-text {
    max-width: 70px;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .chip-remove {
    background: none;
    border: none;
    padding: 0 1px;
    margin-left: 2px;
    font-size: 0.85rem;
    line-height: 1;
    color: inherit;
    opacity: 0.6;
    cursor: pointer;
    border-radius: 2px;
  }

  .chip-remove:hover {
    opacity: 1;
    background: rgba(0, 0, 0, 0.1);
  }

  .chip-all {
    background: #eff6ff;
    color: #1d4ed8;
    border: 1px solid #bfdbfe;
  }

  .chip-more {
    background: var(--tt-slate-100, #f1f5f9);
    color: var(--tt-slate-600, #475569);
    border: 1px solid var(--tt-slate-200, #e2e8f0);
    font-size: 0.65rem;
  }

  .placeholder-text {
    font-size: 0.75rem;
    color: var(--tt-slate-400, #94a3b8);
    font-style: italic;
  }

  .trigger-arrow {
    display: flex;
    align-items: center;
    margin-left: 0.35rem;
    color: var(--tt-slate-400, #94a3b8);
    flex-shrink: 0;
  }

  :global(.chevron) {
    transition: transform 0.2s ease;
  }

  :global(.chevron.rotated) {
    transform: rotate(180deg);
  }

  .status-dropdown-menu {
    position: absolute;
    top: calc(100% + 4px);
    left: 0;
    right: 0;
    min-width: 260px;
    background: #ffffff;
    border: 1px solid var(--tt-slate-200, #e2e8f0);
    border-radius: 8px;
    box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.12), 0 8px 10px -6px rgba(0, 0, 0, 0.08);
    z-index: 100;
    overflow: hidden;
    animation: dropdownFadeIn 0.15s ease-out;
  }

  @keyframes dropdownFadeIn {
    from {
      opacity: 0;
      transform: translateY(-4px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .dropdown-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.5rem 0.75rem;
    background: var(--tt-slate-50, #f8fafc);
    border-bottom: 1px solid var(--tt-slate-200, #e2e8f0);
  }

  .dropdown-heading {
    font-size: 0.7rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--tt-slate-500, #64748b);
  }

  .dropdown-quick-links {
    display: flex;
    align-items: center;
    gap: 0.3rem;
  }

  .quick-link {
    background: none;
    border: none;
    padding: 0;
    font-size: 0.72rem;
    font-weight: 600;
    color: var(--tt-primary, #2563eb);
    cursor: pointer;
    text-decoration: none;
  }

  .quick-link:hover {
    text-decoration: underline;
  }

  .quick-bullet {
    color: var(--tt-slate-300, #cbd5e1);
    font-size: 0.6rem;
  }

  .dropdown-items-list {
    padding: 0.35rem 0;
    max-height: 220px;
    overflow-y: auto;
  }

  .dropdown-item-row {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.45rem 0.75rem;
    cursor: pointer;
    transition: background 0.12s ease;
    user-select: none;
    font-size: 0.8rem;
    font-weight: 500;
    color: var(--tt-slate-700, #334155);
  }

  .dropdown-item-row:hover {
    background: var(--tt-slate-100, #f1f5f9);
  }

  .dropdown-item-row.checked {
    background: #f0f7ff;
    color: #1e3a8a;
    font-weight: 600;
  }

  .status-checkbox {
    width: 14px;
    height: 14px;
    accent-color: var(--tt-primary, #2563eb);
    cursor: pointer;
    margin: 0;
  }

  .status-indicator-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    flex-shrink: 0;
  }

  .status-name {
    flex: 1;
  }

  .status-item-count {
    font-size: 0.72rem;
    color: var(--tt-slate-400, #94a3b8);
    font-weight: 500;
  }

  .status-item-count.has-items {
    color: var(--tt-slate-600, #475569);
    font-weight: 600;
  }

  .search-box {
    position: relative;
    display: flex;
    align-items: center;
    margin-top: 0.65rem;
  }

  :global(.search-icon) {
    position: absolute;
    left: 0.75rem;
    color: var(--tt-slate-400, #94a3b8);
    pointer-events: none;
  }

  .search-input {
    width: 100%;
    padding: 0.45rem 2rem 0.45rem 2.2rem;
    font-size: 0.82rem;
    background: var(--tt-slate-50, #f8fafc);
    border: 1px solid var(--tt-slate-200, #e2e8f0);
    border-radius: 6px;
    color: var(--tt-slate-800, #1e293b);
    transition: all 0.15s ease;
  }

  .search-input:focus {
    background: #ffffff;
    border-color: var(--tt-primary, #2563eb);
    box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.15);
    outline: none;
  }

  .clear-btn {
    position: absolute;
    right: 0.5rem;
    background: none;
    border: none;
    font-size: 1.1rem;
    color: var(--tt-slate-400, #94a3b8);
    cursor: pointer;
    line-height: 1;
    padding: 2px 4px;
    border-radius: 4px;
  }

  .clear-btn:hover {
    color: var(--tt-slate-700, #334155);
  }

  .filter-summary-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: 0.45rem;
    font-size: 0.72rem;
    color: var(--tt-slate-500, #64748b);
  }

  .filter-count-text strong {
    color: var(--tt-slate-800, #1e293b);
  }

  .btn-reset-default {
    background: none;
    border: none;
    padding: 0;
    font-size: 0.72rem;
    font-weight: 600;
    color: var(--tt-primary, #2563eb);
    cursor: pointer;
  }

  .btn-reset-default:hover {
    text-decoration: underline;
  }

  /* Quotes Scroll List */
  .quotes-list-scroll {
    flex: 1;
    overflow-y: auto;
    padding: 0.75rem;
    display: flex;
    flex-direction: column;
    gap: 0.65rem;
  }

  .quote-card {
    display: flex;
    flex-direction: column;
    background: #ffffff;
    border: 1px solid var(--tt-slate-200, #e2e8f0);
    border-radius: 8px;
    padding: 0.85rem;
    cursor: pointer;
    text-align: left;
    transition: all 0.15s ease;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.02);
  }

  .quote-card:hover {
    border-color: #93c5fd;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.06);
    transform: translateY(-1px);
  }

  .quote-card.selected {
    border-color: #2563eb;
    background: #f8fafc;
    box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.25);
  }

  .card-top-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 0.4rem;
  }

  .quote-number-pill {
    font-family: monospace;
    font-weight: 700;
    font-size: 0.82rem;
    color: #1e3a8a;
    background: #dbeafe;
    padding: 0.15rem 0.45rem;
    border-radius: 4px;
  }

  .status-pill {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    font-size: 0.68rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    padding: 0.15rem 0.45rem;
    border-radius: 4px;
  }

  .status-active {
    background: #dcfce7;
    color: #15803d;
  }

  .status-proposed {
    background: #e0f2fe;
    color: #0369a1;
  }

  .status-pending {
    background: #fef3c7;
    color: #b45309;
  }

  .status-completed {
    background: #f1f5f9;
    color: #475569;
  }

  .status-cancelled {
    background: #fee2e2;
    color: #b91c1c;
  }

  .loading-state,
  .error-state,
  .empty-list-state {
    padding: 2rem 1rem;
    text-align: center;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    color: var(--tt-slate-500, #64748b);
  }

  .empty-msg-title {
    font-weight: 600;
    color: var(--tt-slate-700, #334155);
    margin-bottom: 0.25rem;
  }

  .empty-msg-sub {
    font-size: 0.75rem;
    color: var(--tt-slate-500, #64748b);
    max-width: 240px;
    margin: 0 auto;
  }

  .mt-2 {
    margin-top: 0.5rem;
  }

  .sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }

  .job-card-badges {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
  }

  .job-number-pill {
    background: #e0e7ff;
    color: #3730a3;
  }

  .quote-ref-pill {
    font-family: monospace;
    font-size: 0.72rem;
    font-weight: 600;
    color: #64748b;
    background: #f1f5f9;
    padding: 0.1rem 0.35rem;
    border-radius: 4px;
    border: 1px solid #e2e8f0;
  }

  .trucks-val {
    color: #2563eb;
    font-weight: 600;
  }

  .card-client-row {
    margin-bottom: 0.25rem;
  }

  .client-name {
    font-size: 0.95rem;
    font-weight: 700;
    color: var(--tt-slate-900, #0f172a);
  }

  .card-site-preview {
    font-size: 0.76rem;
    color: var(--tt-slate-500, #64748b);
    margin-bottom: 0.45rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .card-materials-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 0.25rem;
    margin-bottom: 0.55rem;
  }

  .mat-chip {
    font-size: 0.7rem;
    font-weight: 600;
    background: #f1f5f9;
    color: #334155;
    padding: 0.1rem 0.35rem;
    border-radius: 3px;
  }

  .mat-chip-more {
    font-size: 0.7rem;
    font-weight: 700;
    color: #64748b;
    padding: 0.1rem 0.3rem;
  }

  .card-footer-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding-top: 0.45rem;
    border-top: 1px solid var(--tt-slate-100, #f1f5f9);
    font-size: 0.75rem;
  }

  .metric-pair {
    display: flex;
    align-items: center;
    gap: 0.25rem;
  }

  .metric-label {
    color: var(--tt-slate-500, #64748b);
  }

  .metric-val {
    font-weight: 600;
    color: var(--tt-slate-800, #1e293b);
  }

  .spinner {
    width: 20px;
    height: 20px;
    border: 2px solid var(--tt-slate-200, #e2e8f0);
    border-top-color: var(--tt-primary, #2563eb);
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
    display: inline-block;
  }

  .btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-weight: 600;
    border-radius: 6px;
    cursor: pointer;
    transition: all 0.15s ease;
    border: 1px solid transparent;
  }

  .btn-xs {
    padding: 0.25rem 0.6rem;
    font-size: 0.75rem;
  }

  .btn-sm {
    padding: 0.35rem 0.8rem;
    font-size: 0.82rem;
  }

  .btn-primary {
    background: var(--tt-primary, #2563eb);
    color: #ffffff;
    border-color: #1d4ed8;
  }

  .btn-primary:hover {
    background: #1d4ed8;
  }

  .btn-outline {
    background: transparent;
    border-color: var(--tt-slate-300, #cbd5e1);
    color: var(--tt-slate-700, #334155);
  }

  .btn-outline:hover {
    background: var(--tt-slate-100, #f1f5f9);
    border-color: var(--tt-slate-400, #94a3b8);
  }

  .btn-ghost {
    background: transparent;
    color: var(--tt-slate-600, #475569);
  }

  .btn-ghost:hover {
    background: var(--tt-slate-100, #f1f5f9);
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
</style>
